'use client';

import { useState, useEffect } from 'react';
import { useSearchParams, useRouter } from 'next/navigation';

// Static category tabs
const PROJECT_CATEGORIES = [
  { id: 'all', name: 'All Projects', value: 'all' },
  { id: 'websites', name: 'Websites / Apps', value: 'websites' },
  { id: 'images', name: 'Images', value: 'images' },
  { id: 'videos', name: 'Videos', value: 'videos' },
  { id: 'music', name: 'Music / Audio', value: 'music' }
];

type ViewMode = 'grid' | 'list';

export default function ProjectsCategoriesStatic() {
  const searchParams = useSearchParams();
  const router = useRouter();
  
  const [activeTab, setActiveTab] = useState('all');
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  
  // Initialize from URL
  useEffect(() => {
    const tab = searchParams.get('tab') || 'all';
    const view = searchParams.get('view') as ViewMode || 'grid';
    setActiveTab(tab);
    setViewMode(view);
  }, [searchParams]);
  
  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    const url = new URL(window.location.href);
    if (tab === 'all') {
      url.searchParams.delete('tab');
    } else {
      url.searchParams.set('tab', tab);
    }
    router.push(url.pathname + url.search, { scroll: false });
  };
  
  const handleViewModeChange = (mode: ViewMode) => {
    setViewMode(mode);
    const url = new URL(window.location.href);
    url.searchParams.set('view', mode);
    router.push(url.pathname + url.search, { scroll: false });
  };
  
  return (
    <div className="w-full flex justify-center">
      <section className="relative w-full max-w-[1440px] bg-white border-t border-black/10 px-6 sm:px-8 lg:px-12">
        <div className="flex justify-between items-center py-6">
          {/* Tabs */}
          <div className="flex gap-8">
            {PROJECT_CATEGORIES.map((category) => (
              <button
                key={category.id}
                onClick={() => handleTabChange(category.value)}
                className={`font-ibm-plex-sans-condensed text-[16px] font-medium tracking-[1.6px] uppercase pb-2 transition-all ${
                  activeTab === category.value
                    ? 'text-black border-b-2 border-black'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {category.name}
              </button>
            ))}
          </div>
          
          {/* View Mode Toggle */}
          <div className="flex gap-2">
            <button
              onClick={() => handleViewModeChange('grid')}
              className={`p-2 rounded ${viewMode === 'grid' ? 'bg-gray-200' : 'hover:bg-gray-100'}`}
              aria-label="Grid view"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <rect x="3" y="3" width="7" height="7" strokeWidth="2" />
                <rect x="14" y="3" width="7" height="7" strokeWidth="2" />
                <rect x="3" y="14" width="7" height="7" strokeWidth="2" />
                <rect x="14" y="14" width="7" height="7" strokeWidth="2" />
              </svg>
            </button>
            <button
              onClick={() => handleViewModeChange('list')}
              className={`p-2 rounded ${viewMode === 'list' ? 'bg-gray-200' : 'hover:bg-gray-100'}`}
              aria-label="List view"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <line x1="3" y1="6" x2="21" y2="6" strokeWidth="2" />
                <line x1="3" y1="12" x2="21" y2="12" strokeWidth="2" />
                <line x1="3" y1="18" x2="21" y2="18" strokeWidth="2" />
              </svg>
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}